import { Loader } from '@googlemaps/js-api-loader';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import createCustomMarker from './custom-marker';
import createClusterMarker from './cluster-marker';
import createAssetMarker from './asset-marker';

const GOOGLE_MAPS_API_KEY = 'AIzaSyA8Ihrp4YxglEPZclNTGDNf-9omGNwinC4';
const INIT_ZOOM_LEVEL = 2;
const CLUSTER_IMG_PATH = 'Content/google-maps/m';
const POLYGON_SETTINGS = {
  strokeColor: '#1990bf',
  fillColor: '#1990bf',
  fillOpacity: 0.35,
};
const MAP_OPTIONS = {
  center: {
    lat: 0,
    lng: 0,
  },
  zoom: INIT_ZOOM_LEVEL,
  minZoom: 3,
  mapTypeId: 'roadmap',
  styles: [
    {
      featureType: 'all',
      elementType: 'geometry.fill',
      stylers: [
        {
          weight: '2.00',
        },
      ],
    },
    {
      featureType: 'all',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#9c9c9c',
        },
      ],
    },
    {
      featureType: 'all',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'all',
      stylers: [
        {
          color: '#f2f2f2',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'all',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'all',
      stylers: [
        {
          saturation: -100,
        },
        {
          lightness: 45,
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#7b7b7b',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'all',
      stylers: [
        {
          visibility: 'simplified',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'all',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'all',
      stylers: [
        {
          color: '#46bcec',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#c8d7d4',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#070707',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
  ],
};

class GoogleMapsService {
  constructor() {
    this.loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places', 'geometry', 'drawing'],
    });

    this.map = null;
    this.markers = [];
    this.drawingManager = null;
    this.polygons = [];
    this.markerOverlays = [];
    this.assetMap = null;
    //hold ref to its component
    this.vm = null;
    this.scriptLoaded = false;
    this.loadMapScript();
  }

  loadMapScript() {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded) {
        resolve();
      } else {
        this.loader
          .load()
          .then(() => {
            this.scriptLoaded = true;
            resolve();
          })
          .catch((e) => {
            reject(e);
          });
      }
    });
  }

  createMap(options = {}) {
    return new Promise((resolve, reject) => {
      this.loadMapScript()
        .then(() => {
          const { el } = options;

          if (el !== null) {
            const { popupEl, allowEditGeoZone, mapType } = options;
            const mapTypeId = mapType === 'satellite' ? 'satellite' : 'roadmap';
            const mapOptions = { ...MAP_OPTIONS, mapTypeId };
            this.map = new google.maps.Map(el, mapOptions);

            this.markerCluster = new MarkerClusterer(this.map, [], {
              imagePath: CLUSTER_IMG_PATH,
              clusterClass: 'site-marker-hidden',
            });

            this.markerCluster.addListener('clusteringend', (mc) => {
              this.renderMarkers(mc);
            });

            this.allowEditGeoZone = allowEditGeoZone || false;

            if (allowEditGeoZone) this.initDrawingManager();

            this.infoPopup = new google.maps.InfoWindow({
              content: popupEl || '',
            });

            google.maps.event.addListener(this.map, 'click', () => {
              this.infoPopup.close();
            });
          }

          resolve();
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  initDrawingManager() {
    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYGON,
          google.maps.drawing.OverlayType.RECTANGLE,
        ],
      },
      polygonOptions: {
        editable: true,
        ...POLYGON_SETTINGS,
      },
      rectangleOptions: {
        editable: true,
        ...POLYGON_SETTINGS,
      },
    });

    const handlePolygonComplete = (polygon) => {
      this.drawingManager.setDrawingMode(null);

      if (this.polygons.length > 0) {
        this.polygons.forEach((item) => {
          item.setMap(null);
        });

        this.polygons = [];
      }

      this.polygons.push(polygon);
      this.initPolygon(polygon);
    };

    const polygonComplete = (polygon) => {
      handlePolygonComplete(polygon);
      this.vm.showApplyChanges();
    };
    const rectangleComplete = (rectangle) => {
      //convert rectangle to polygon
      const ne = rectangle.getBounds().getNorthEast();
      const sw = rectangle.getBounds().getSouthWest();
      const points = [
        [sw.lng(), ne.lat()],
        [sw.lng(), sw.lat()],
        [ne.lng(), sw.lat()],
        [ne.lng(), ne.lat()],
      ];
      const polyPoints = (points || []).map(([lng, lat]) => new google.maps.LatLng(lat, lng));
      rectangle.setMap(null);
      const polygon = new google.maps.Polygon({
        path: polyPoints,
        ...POLYGON_SETTINGS,
      });

      polygon.setMap(this.map);
      polygonComplete(polygon);
    };

    //TODO move these events to the map component
    google.maps.event.addListener(this.drawingManager, 'polygoncomplete', polygonComplete);
    google.maps.event.addListener(this.drawingManager, 'rectanglecomplete', rectangleComplete);

    this.drawingManager.setMap(this.map);

    google.maps.event.addListener(this.map, 'click', () => {
      this.polygons.forEach((item) => {
        this.setPolygonEditable(item, false);
      });
    });
  }

  setPolygonsEditMode(mode = false) {
    this.polygons.forEach((item) => {
      this.setPolygonEditable(item, mode);
    });
  }

  setPolygonEditable(polygon, mode = false) {
    if (!polygon) return;
    const polygonUpdated = () => {
      this.vm.showApplyChanges();
    };

    const path = polygon.getPath();

    if (mode) {
      polygon.setEditable(true);
      google.maps.event.addListener(path, 'set_at', polygonUpdated);
      google.maps.event.addListener(path, 'insert_at', polygonUpdated);
      google.maps.event.addListener(path, 'remove_at', polygonUpdated);
    } else {
      polygon.setEditable(false);
      google.maps.event.clearListeners(path, 'set_at');
      google.maps.event.clearListeners(path, 'insert_at');
      google.maps.event.clearListeners(path, 'remove_at');
    }
  }

  initPolygon(polygon) {
    polygon.setEditable(false);
    google.maps.event.addListener(polygon, 'click', () => {
      this.setPolygonEditable(polygon, true);
    });
  }

  setMarkers(assets) {
    this.assetMap = new Map();
    this.clearMarkers();
    let bounds = new google.maps.LatLngBounds();

    if (!assets || assets.length === 0) {
      // fit the map to polygon
      if ((this.polyPoints || []).length) {
        this.polyPoints.forEach((pos) => {
          bounds.extend(pos);
        });
        this.map.fitBounds(bounds);
      }
      return;
    }

    const onSiteMarkersPosition = [];
    const offSiteMarkersPosition = [];

    assets.forEach((asset) => {
      if (asset.lat && asset.lng) {
        const marker = new google.maps.Marker({
          position: {
            lat: asset.lng,
            lng: asset.lat,
          },
          map: this.map,
          visible: false,
        });

        this.markers.push(marker);
        this.assetMap.set(marker, asset);

        if (asset.onSite) {
          onSiteMarkersPosition.push(marker.position);
        } else {
          offSiteMarkersPosition.push(marker.position);
        }
      }
    });

    this.markerCluster.addMarkers(this.markers);

    // fit the map to polygon bound or the assets
    const positions = onSiteMarkersPosition.length
      ? this.polyPoints && this.polyPoints.length
        ? this.polyPoints
        : onSiteMarkersPosition
      : offSiteMarkersPosition;

    positions.forEach((pos) => {
      bounds.extend(pos);
    });
    this.map.fitBounds(bounds);
    // this.map.setZoom(this.map.getZoom() + 1);
  }

  clearMarkers() {
    this.markers.forEach((marker) => {
      marker.setMap(null);
    });

    this.markers = [];

    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      (this.markerOverlays || []).forEach((item) => item.setMap(null));
      this.markerOverlays = [];
    }
  }

  /**
   * Since google maps only supports img marker, hide google maps markers and show
   * customized overlays as markers
   *
   * @param mc
   */
  renderMarkers(mc) {
    this.markerOverlays.forEach((item) => item.setMap(null));
    this.markerOverlays = [];

    mc.clusters_.forEach((cluster) => {
      if (cluster.markers_.length > 1) {
        const { data, info } = this.getClusterData(cluster.markers_);
        const marker = createCustomMarker({
          latlng: cluster.center_,
          map: this.map,
          html: createClusterMarker(data),
          infoPopup: this.infoPopup,
          info,
        });

        google.maps.event.addListener(marker, 'click', () => {
          const bounds = new google.maps.LatLngBounds();

          cluster.markers_.forEach((item) => {
            bounds.extend(item.position);
          });

          this.map.fitBounds(bounds);
        });

        this.markerOverlays.push(marker);
      } else {
        let marker = cluster.markers_[0];
        const asset = this.assetMap.get(marker);
        marker = createCustomMarker({
          latlng: marker.position,
          map: this.map,
          html: createAssetMarker(asset),
          infoPopup: this.infoPopup,
          info: `<h4 class="z-h4">${asset.name}</h4>`,
        });

        this.markerOverlays.push(marker);
      }
    });
  }

  setGeoZone(geometry) {
    if (geometry) {
      this.clearGeoZone();

      const [points] = (wellknown.parse(geometry) || {}).coordinates;
      this.polyPoints = (points || []).map(([lng, lat]) => new google.maps.LatLng(lat, lng));

      const polygon = new google.maps.Polygon({
        path: this.polyPoints,
        ...POLYGON_SETTINGS,
      });

      polygon.setMap(this.map);
      this.polygons.push(polygon);

      if (this.allowEditGeoZone) {
        this.initPolygon(polygon);
      }
    }
  }

  clearGeoZone() {
    if (this.polygons.length > 0) {
      this.polygons.forEach((item) => {
        item.setMap(null);
      });

      this.polygons = [];
    }
  }

  getClusterData(markers) {
    const assetsByType = {};
    const data = { data: null, info: '<div class="cluster-popup">' };

    markers.forEach((marker) => {
      const asset = this.assetMap.get(marker);

      if (asset) {
        let { assetType } = asset;
        assetType = assetType.toLowerCase();

        if (assetsByType[assetType]) assetsByType[assetType].push(asset);
        else assetsByType[assetType] = [asset];

        data.info += `<p class="z-h6 ${assetType}">${asset.name}</p>`;
      }
    });

    data.data = Object.keys(assetsByType).map((key) => ({ key, value: assetsByType[key].length }));
    data.info += '</div>';

    return data;
  }

  getPolygonGeometry() {
    /**
     * to match the current geozone in our system,
     * @param {*} points
     * @returns
     */
    const getPolygonString = (points = []) => {
      const GeoJSON = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: points,
        },
      };
      return wellknown.stringify(GeoJSON);
    };
    //so far will handle one polygon
    const [polygon] = this.polygons || [];
    const points = polygon
      ? polygon
          .getPaths()
          .getArray()
          .map((path) => path.getArray().map((p) => [p.lng(), p.lat()]))
      : undefined;
    return points ? getPolygonString(points) : undefined;
  }

  /**
   * test if lat lng inside google map polygon
   * @param {*} lat
   * @param {*} lng
   * @param {*} polygon
   * @returns
   */
  isPointInsideGaPolygon(lat, lng, polygon) {
    //if unknown position => it maybe onsite
    if (!lat || !lng) return true;
    if (!polygon) return true;
    const point = new google.maps.LatLng(lng, lat);

    return google.maps.geometry.poly.containsLocation(point, polygon);
  }

  /**
   * build google map polygon from geometry string
   * @param {*} geometry
   * @returns
   */
  getPolygon(geometry) {
    if (!geometry) return undefined;
    const [points] = (wellknown.parse(geometry) || {}).coordinates || [];
    if (!points || !points.length) return undefined;
    const polyPoints = (points || []).map(([lng, lat]) => new google.maps.LatLng(lat, lng));

    return new google.maps.Polygon({
      path: polyPoints,
    });
  }

  /**
   * set location search
   */
  setLocationSearch(searchInput) {
    const autocomplete = new google.maps.places.Autocomplete(searchInput);
    autocomplete.bindTo('bounds', this.map);
    autocomplete.setFields(['geometry']);
    const handleLocationSearch = (e) => {
      const place = autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) {
        return;
      }

      if (place.geometry.viewport) {
        this.map.fitBounds(place.geometry.viewport);
      } else {
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(17);
      }
    };
    autocomplete.addListener('place_changed', handleLocationSearch);
  }
}

export default new GoogleMapsService();
